import React, { useState, useEffect, useCallback } from 'react';
import { api as axios } from '../utils/apiClient';
import { Link } from 'react-router-dom';
import Sidebar from './Sidebar';
import './SiteMonitoring.css';

const SiteMonitoring = () => {
  const [sites, setSites] = useState([]);
  const [monitorData, setMonitorData] = useState({});
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState({});
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');

  const fetchSites = useCallback(async () => {
    try {
      setLoading(true);
      console.log('[SiteMonitoring] Fetching sites list');
      const { data } = await axios.get('/api/sites');
      setSites(data);
      console.log('[SiteMonitoring] Sites loaded', data.map(site => ({ id: site._id, url: site.url })));
      
      // Fetch monitoring data for all sites
      const monitorPromises = data.map(async (site) => {
        const endpoint = `/api/sites/${site._id}/monitor?force=true`;
        console.log('[SiteMonitoring] Fetching monitor data', { siteId: site._id, endpoint });
        try {
          const monitorResponse = await axios.get(endpoint);
          const payload = monitorResponse.data;
          console.log('[SiteMonitoring] Monitor payload', { siteId: site._id, payload });
          if (payload?.success === false && !payload?.data) {
            return { siteId: site._id, data: null, error: payload.error || 'Unable to load site details' };
          }
          return {
            siteId: site._id,
            data: payload?.data || payload,
            lastSync: payload?.lastSync || payload?.data?.timestamp || new Date().toISOString(),
            error: payload?.error,
            status: payload?.status || (payload?.cached ? 'cached' : 'live'),
            statusDetails: payload?.status_details || null
          };
        } catch (err) {
          console.error(`Error fetching monitor data for site ${site._id}:`, err);
          return { siteId: site._id, data: null, error: err.message };
        }
      });
      
      const results = await Promise.all(monitorPromises);
      const monitorMap = {};
      results.forEach((result) => {
        if (result.data) {
          monitorMap[result.siteId] = {
            data: result.data,
            lastSync: result.lastSync || result.data?.timestamp || null,
            error: result.error || null,
            status: result.status || (result.cached ? 'cached' : 'live'),
            statusDetails: result.status_details || null
          };
        } else {
          monitorMap[result.siteId] = { error: result.error || 'No monitoring data yet', status: 'unavailable' };
        }
      });
      setMonitorData(monitorMap);
    } catch (err) {
      console.error('Error fetching sites:', err);
      setError('Failed to load sites');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSites();
    // Auto-refresh every 5 minutes
    const interval = setInterval(fetchSites, 300000);
    return () => clearInterval(interval);
  }, [fetchSites]);

  const syncSite = async (siteId) => {
    try {
      console.log('[SiteMonitoring] Sync requested', { siteId });
      setSyncing({ ...syncing, [siteId]: true });
      const response = await axios.post(`/api/sites/${siteId}/sync-monitor`);

      if (response.data?.success === false) {
        setMonitorData({
          ...monitorData,
          [siteId]: {
            error: response.data.error || 'Failed to sync site monitoring data',
            status: response.data.status || 'unavailable',
            statusDetails: response.data.status_details || null
          }
        });
        alert(response.data.error || 'Failed to sync site monitoring data');
      } else {
        setMonitorData({
          ...monitorData,
          [siteId]: {
            data: response.data?.data || response.data,
            lastSync: response.data?.data?.timestamp || new Date().toISOString(),
            error: response.data?.error || null,
            status: response.data?.status || 'live',
            statusDetails: response.data?.status_details || null
          }
        });
      }
    } catch (err) {
      console.error('Error syncing site:', err);
      alert('Failed to sync site monitoring data');
      setMonitorData({
        ...monitorData,
        [siteId]: { error: err.message || 'Failed to sync site monitoring data', status: 'unavailable' }
      });
    } finally {
      setSyncing({ ...syncing, [siteId]: false });
    }
  };

  const getPerformanceColor = (score) => {
    if (score === null || score === undefined) return '#999';
    if (score >= 90) return '#28a745';
    if (score >= 50) return '#ffc107';
    return '#dc3545';
  };

  const getHealthStatus = (health) => {
    if (!health || !health.available) {
      return { status: 'unknown', text: 'N/A', color: '#999' };
    }
    const critical = health.critical_issues_count || 0;
    const recommended = health.recommended_count || 0;
    
    if (critical > 0) {
      return { status: 'critical', text: `${critical} critical`, color: '#dc3545' };
    }
    if (recommended > 0) {
      return { status: 'warning', text: `${recommended} recommended`, color: '#ffc107' };
    }
    return { status: 'good', text: 'Good', color: '#28a745' };
  };

  const buildStatusTooltip = (status, details, lastSync) => {
    const succeeded = details?.succeeded_endpoints || [];
    const failed = details?.failed_endpoints || [];
    const lines = [
      `Status: ${status?.toUpperCase() || 'UNKNOWN'}`,
      `Last updated: ${details?.last_updated ? new Date(details.last_updated).toLocaleString() : (lastSync ? new Date(lastSync).toLocaleString() : 'N/A')}`,
      `Source: ${
        status === 'live'
          ? 'Live'
          : status === 'cached'
          ? 'Cache'
          : status === 'partial'
          ? 'Partial'
          : 'Unavailable'
      }`,
    ];
    if (succeeded.length) {
      lines.push('✔ ' + succeeded.join('\n✔ '));
    }
    if (failed.length) {
      lines.push('✖ ' + failed.join('\n✖ '));
    }
    return lines.join('\n');
  };

  const filteredSites = sites.filter(site => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      site.name?.toLowerCase().includes(searchLower) ||
      site.url?.toLowerCase().includes(searchLower)
    );
  });

  if (loading) {
    return (
      <div className="dashboard-container">
        <Sidebar />
        <div className="monitoring-page">
          <div className="loading">Loading monitoring data...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <Sidebar />
      <div className="monitoring-page">
        <div className="monitoring-header">
          <h1>Site Overview</h1>
          <div className="header-actions">
            <input
              type="text"
              placeholder="Search sites..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="search-input"
            />
            <button onClick={fetchSites} className="btn-refresh-all">
              🔄 Refresh All
            </button>
          </div>
        </div>

        {error && (
          <div className="error-banner">
            {error}
          </div>
        )}

        <div className="monitoring-grid">
          {filteredSites.map((site) => {
            const entry = monitorData[site._id] || {};
            const siteData = entry.data || (entry.success && entry) || {};
            const hasError = !!entry.error && !siteData;
            const showWarning = !!entry.error && !!siteData;
            const errorMessage = entry.error;
            const healthStatus = siteData?.site_health ? getHealthStatus(siteData.site_health) : null;
            const lastSync = entry.lastSync || siteData?.timestamp || null;
            const status = entry.status || 'unavailable';
            const statusDetails = entry.statusDetails || entry.status_details || null;
            const statusTooltip = buildStatusTooltip(status, statusDetails, lastSync);
            // Performance scores:
            // Prefer PageSpeed mobile/desktop scores when available and
            // fall back to helper-plugin overall performance_score.
            const mobileScore =
              site?.performanceData?.mobile?.performanceScore?.value ?? null;
            const desktopScore =
              site?.performanceData?.desktop?.performanceScore?.value ?? null;
            const overallScore =
              siteData?.performance_score !== null &&
              siteData?.performance_score !== undefined
                ? siteData.performance_score
                : null;
            const colorScore =
              mobileScore !== null && mobileScore !== undefined
                ? mobileScore
                : desktopScore !== null && desktopScore !== undefined
                ? desktopScore
                : overallScore;
            
            return (
              <div key={site._id} className="monitoring-card">
                <div className="card-header">
                  <h3>{site.name || site.url}</h3>
                  <div className="card-actions">
                    <span className={`status-badge status-${status}`} title={statusTooltip}>
                      {status.toUpperCase()}
                    </span>
                    <button
                      onClick={() => syncSite(site._id)}
                      disabled={syncing[site._id]}
                      className="btn-sync"
                      title="Sync monitoring data"
                    >
                      {syncing[site._id] ? '⏳' : '🔄'}
                    </button>
                    <Link
                      to={`/sites/${site._id}`}
                      className="btn-view"
                      title="View site details"
                    >
                      View
                    </Link>
                  </div>
                </div>

                <div className="card-url">{site.url}</div>

                {hasError ? (
                  <div className="card-error">
                    <p>{errorMessage || 'Failed to load monitoring data'}</p>
                    <button
                      onClick={() => syncSite(site._id)}
                      className="btn-sync-now"
                      disabled={syncing[site._id]}
                    >
                      {syncing[site._id] ? 'Syncing...' : 'Sync Now'}
                    </button>
                  </div>
                ) : siteData ? (
                  <>
                    {showWarning && (
                      <div className="card-error warning-inline">
                        <p>{errorMessage}</p>
                      </div>
                    )}
                    <div className="monitoring-info-grid">
                      <div className="info-item">
                        <span className="info-label">WP Version</span>
                        <span className="info-value">{siteData.wp_version || 'N/A'}</span>
                      </div>
                      <div className="info-item">
                        <span className="info-label">PHP Version</span>
                        <span className="info-value">{siteData.php_version || 'N/A'}</span>
                      </div>
                      <div className="info-item">
                        <span className="info-label">Active Theme</span>
                        <span className="info-value">{siteData.active_theme?.name || 'N/A'}</span>
                      </div>
                      <div className="info-item">
                        <span className="info-label">Plugins</span>
                        <span className="info-value">{siteData.plugins?.length ?? siteData.plugins_count ?? 0}</span>
                      </div>
                      <div className="info-item">
                        <span className="info-label">Site Health</span>
                        <span 
                          className="info-value"
                          style={{ color: healthStatus?.color || '#999' }}
                        >
                          {healthStatus?.text || 'N/A'}
                        </span>
                      </div>
                      <div className="info-item">
                        <span className="info-label">Performance (Mobile / Desktop)</span>
                        <span 
                          className="info-value"
                          style={{ color: getPerformanceColor(colorScore) }}
                        >
                          {mobileScore !== null || desktopScore !== null ? (
                            <>
                              {`M ${
                                mobileScore !== null && mobileScore !== undefined
                                  ? Math.round(mobileScore)
                                  : 'N/A'
                              }/100 · D ${
                                desktopScore !== null && desktopScore !== undefined
                                  ? Math.round(desktopScore)
                                  : 'N/A'
                              }/100`}
                            </>
                          ) : overallScore !== null && overallScore !== undefined ? (
                            `${Math.round(overallScore)}/100`
                          ) : (
                            'N/A'
                          )}
                        </span>
                      </div>
                    </div>

                    {lastSync && (
                      <div className="card-footer">
                        <span>Last sync: {new Date(lastSync).toLocaleString()}</span>
                        <span className="status-source">
                          Source: {status === 'live' ? 'Live' : status === 'cached' ? 'Cache' : status === 'partial' ? 'Partial' : 'Unavailable'}
                        </span>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="no-data">
                    <p>No monitoring data available</p>
                    <button
                      onClick={() => syncSite(site._id)}
                      className="btn-sync-now"
                      disabled={syncing[site._id]}
                    >
                      {syncing[site._id] ? 'Syncing...' : 'Sync Now'}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {filteredSites.length === 0 && (
          <div className="empty-state">
            <p>{search ? 'No sites found matching your search' : 'No sites added yet'}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SiteMonitoring;

